import type {
  ConnectorConnection,
  ConnectorProvider,
} from '@saaga/shared/interfaces/connector.interface'
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

import { ConnectorProviderCard } from './connector-provider-card.molecule'

export interface ConnectorProviderListProps {
  providerList: ConnectorProvider[]
  connectionList: ConnectorConnection[]
  connectingProviderSlug: string | null
  onConnect: (providerSlug: string) => void
  onDisconnect: (providerSlug: string) => void
  onProviderClick?: (providerSlug: string) => void
  className?: string
}

const findConnectionForProvider = (
  connectionList: ConnectorConnection[],
  providerSlug: string
): ConnectorConnection | undefined => connectionList.find((c) => c.provider === providerSlug)

/**
 * Renders a list of connector provider cards with their connection status.
 * Matches each provider to its active connection to display account info.
 */
export const ConnectorProviderList = ({
  providerList,
  connectionList,
  connectingProviderSlug,
  onConnect,
  onDisconnect,
  onProviderClick,
  className,
}: ConnectorProviderListProps): ReactNode => (
  <div className={cn('connector-provider-list grid grid-cols-1 gap-4 md:grid-cols-2', className)}>
    {providerList.map((provider) => {
      const connection = findConnectionForProvider(connectionList, provider.slug)
      return (
        <ConnectorProviderCard
          key={provider.slug}
          providerSlug={provider.slug}
          providerDisplayName={provider.displayName}
          isConnected={provider.isConnected}
          connectedAccountEmail={connection?.providerAccountId ?? null}
          serviceTypeList={provider.serviceTypeList}
          isConnecting={connectingProviderSlug === provider.slug}
          onConnect={onConnect}
          onDisconnect={onDisconnect}
          onClick={onProviderClick ? () => onProviderClick(provider.slug) : undefined}
        />
      )
    })}
  </div>
)

ConnectorProviderList.displayName = 'ConnectorProviderList'
